import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Link,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Work as JobIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Upload as UploadIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface Job {
  job_id: string;
  platform: string;
  status: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  platform_response?: any;
  metadata?: any;
}

interface AdJobsSectionProps {
  adId: string;
}

const AdJobsSection: React.FC<AdJobsSectionProps> = ({ adId }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [transcript, setTranscript] = useState<any | null>(null);
  const [dropbox, setDropbox] = useState<any | null>(null);

  const loadJobs = async () => {
    try {
      setError(null);
      const [jobsResp, ad] = await Promise.all([
        apiService.getAdJobs(adId),
        apiService.getAd(adId),
      ]);
      setJobs(jobsResp.jobs);
      setTranscript((ad as any).transcript || null);
      setDropbox((ad as any).dropbox || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    
    // Set up automatic refresh every 30 seconds if there are any non-completed jobs
    const interval = setInterval(() => {
      loadJobs();
    }, 30000);
    
    setRefreshInterval(interval);
    
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adId]);

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'info';
      case 'processing': return 'warning';
      case 'uploading': return 'warning';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      case 'pending': return <ScheduleIcon />;
      case 'processing': return <UploadIcon />;
      case 'uploading': return <UploadIcon />;
      default: return <JobIcon />;
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case 'youtube': return <YouTubeIcon sx={{ color: '#FF0000' }} />;
      case 'meta': return <FacebookIcon sx={{ color: '#1877F2' }} />;
      default: return <JobIcon />;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getYouTubeVideoUrl = (job: Job) => {
    const videoId = job.platform_response?.video_id || job.platform_response?.id;
    return videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;
  };

  const getMetaAdUrl = (job: Job) => {
    const adId = job.platform_response?.ad_id;
    return adId ? `https://www.facebook.com/ads/library/?id=${adId}` : null;
  };

  if (loading) {
    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" py={3}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography>Loading jobs...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
          <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          sx={{ cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box display="flex" alignItems="center">
            <JobIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Jobs ({jobs.length + (transcript ? 1 : 0) + (dropbox ? 1 : 0)})
            </Typography>
          </Box>
          <IconButton>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Transcription summary row */}
            {transcript && (
              <List sx={{ mb: 2 }}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <UploadIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">Transcription</Typography>
                        <Chip
                          label={transcript.status || 'unknown'}
                          color={getStatusColor(transcript.status || 'pending')}
                          size="small"
                          icon={getStatusIcon(transcript.status || 'pending')}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {transcript.language_code ? `Language: ${transcript.language_code}` : 'Language: detecting…'}
                        </Typography>
                        {transcript.links?.txt_url && (
                          <Box sx={{ mt: 1 }}>
                            <Link href={transcript.links.txt_url} target="_blank" rel="noopener noreferrer" underline="hover">
                              Download transcript (txt)
                            </Link>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            )}

            {/* Dropbox upload summary row */}
            {dropbox && (
              <List sx={{ mb: 2 }}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <UploadIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">Dropbox Upload</Typography>
                        <Chip
                          label={dropbox.status || 'unknown'}
                          color={getStatusColor(dropbox.status || 'pending')}
                          size="small"
                          icon={getStatusIcon(dropbox.status || 'pending')}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {dropbox.status === 'success' && dropbox.path && (
                          <Typography variant="body2" color="text.secondary">
                            Path: {dropbox.path}
                          </Typography>
                        )}
                        {dropbox.status === 'failed' && dropbox.error && (
                          <Typography variant="body2" color="error">
                            Error: {dropbox.error}
                          </Typography>
                        )}
                        {dropbox.status === 'processing' && (
                          <Typography variant="body2" color="text.secondary">
                            Upload in progress…
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            )}

            {/* Platform jobs */}
            {(jobs.length === 0) ? (
              <Box textAlign="center" py={3}>
                <JobIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  No platform jobs yet
                </Typography>
              </Box>
            ) : (
              <List>
                {jobs.map((job, index) => {
                  const youtubeUrl = getYouTubeVideoUrl(job);
                  const metaUrl = getMetaAdUrl(job);
                  const platformUrl = youtubeUrl || metaUrl;

                  return (
                    <React.Fragment key={job.job_id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon>
                          {getPlatformIcon(job.platform)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle1">
                                {job.platform} Upload
                              </Typography>
                              <Chip
                                label={job.status}
                                color={getStatusColor(job.status)}
                                size="small"
                                icon={getStatusIcon(job.status)}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Started: {formatDateTime(job.created_at)}
                              </Typography>
                              {job.updated_at !== job.created_at && (
                                <Typography variant="body2" color="text.secondary">
                                  Updated: {formatDateTime(job.updated_at)}
                                </Typography>
                              )}
                              {job.error_message && (
                                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                                  Error: {job.error_message}
                                </Typography>
                              )}
                              {platformUrl && (
                                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                                  <Link
                                    href={platformUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                  >
                                    View on {job.platform}
                                  </Link>
                                  <Tooltip title="Copy link">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => copyToClipboard(platformUrl)}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Job ID: {job.job_id}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < jobs.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AdJobsSection;
